import { Box, Typography, makeStyles } from "@material-ui/core";
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { S3Service } from "../../../services/S3Service";
import { awsSettings } from "../../constants";
import UserContext from "../../UserContext";
import { ReportHeaderProps } from "./HeaderPreferences";

type Props = {
    orgInfo: ReportHeaderProps;
    setOrgInfo?: Dispatch<SetStateAction<ReportHeaderProps>>;
    /** Heavier print weight — used for payment receipt header vs other reports */
    boldPrint?: boolean;
};

const useStyles = makeStyles(() => ({
    root: {
        backgroundColor: "#ffffff",
        color: "#1a1a1a",
        padding: "22px 28px 18px",
        printColorAdjust: "exact",
        WebkitPrintColorAdjust: "exact",
        "@media print": {
            backgroundColor: "#ffffff !important",
            color: "#000000 !important",
            padding: "8px 0 10px",
            "-webkit-print-color-adjust": "exact",
            printColorAdjust: "exact",
            colorAdjust: "exact",
        },
    },
    layoutLeft: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 24,
    },
    layoutCenter: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 14,
    },
    layoutRight: {
        display: "flex",
        flexDirection: "row-reverse",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 24,
        textAlign: "right",
    },
    logoCol: {
        flexShrink: 0,
        width: 76,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    logo: {
        width: 64,
        height: 64,
        objectFit: "contain",
        "@media print": {
            width: 48,
            height: 48,
            filter: "grayscale(100%) contrast(1.2)",
        },
    },
    logoPlaceholder: {
        width: 64,
        height: 64,
        borderRadius: 8,
        border: "1px dashed #cfd8dc",
        backgroundColor: "#f8f9fa",
        "@media print": {
            display: "none",
        },
    },
    centerCol: {
        flex: 1,
        minWidth: 0,
    },
    centerColLeft: {
        textAlign: "left",
    },
    centerColCenter: {
        textAlign: "center",
    },
    centerColRight: {
        textAlign: "right",
    },
    rightCol: {
        flexShrink: 0,
        maxWidth: 220,
        textAlign: "right",
    },
    reportTitle: {
        fontFamily: "'Poppins', 'Google Sans', Inter, sans-serif",
        fontWeight: 800,
        fontSize: "1.65rem",
        lineHeight: 1.15,
        letterSpacing: "0.03em",
        color: "#1e293b",
        textTransform: "uppercase",
        "@media print": {
            color: "#000000 !important",
            fontSize: "1.25rem !important",
            fontWeight: 900,
        },
    },
    metaLine: {
        marginTop: 8,
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        color: "#64748b",
        fontFamily: "'Roboto Mono', 'Courier New', monospace",
        textTransform: "uppercase",
        lineHeight: 1.5,
        "@media print": {
            color: "#000000 !important",
            fontSize: "0.7rem !important",
            fontWeight: 700,
        },
    },
    sideOrgName: {
        fontFamily: "'Poppins', 'Google Sans', Inter, sans-serif",
        fontWeight: 700,
        fontSize: "1.15rem",
        lineHeight: 1.3,
        color: "#1e293b",
        "@media print": {
            color: "#000000 !important",
            fontWeight: 800,
        },
    },
    bottomRule: {
        marginTop: 18,
        height: 1,
        backgroundColor: "#e2e8f0",
        "@media print": {
            marginTop: 10,
            height: 0,
            backgroundColor: "transparent !important",
            borderBottom: "2px solid #000000",
        },
    },
    boldPrintTitle: {
        "@media print": {
            fontSize: "1.55rem !important",
            fontWeight: 900,
            letterSpacing: "0.04em",
            textShadow: "0 0 0.4px #000, 0 0 0.4px #000",
        },
    },
    boldPrintMeta: {
        "@media print": {
            fontFamily: "'Poppins', 'Google Sans', Arial, sans-serif !important",
            fontSize: "0.95rem !important",
            fontWeight: 900,
            letterSpacing: "0.03em",
            textShadow: "0 0 0.5px #000, 0 0 0.5px #000, 0 0 0.5px #000",
            color: "#000000 !important",
            opacity: "1 !important",
            WebkitTextStroke: "0.35px #000",
        },
    },
    boldPrintOrg: {
        "@media print": {
            fontSize: "1.35rem !important",
            fontWeight: 900,
            textShadow: "0 0 0.4px #000, 0 0 0.4px #000",
        },
    },
    boldPrintRule: {
        "@media print": {
            borderBottom: "3px solid #000000 !important",
        },
    },
}));

const formatGeneratedAt = () =>
    new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(new Date());

export function ReportHeader({ orgInfo, boldPrint = false }: Props) {
    const classes = useStyles();
    const { store, settings, user } = useContext(UserContext);
    const [logoUrl, setLogoUrl] = useState("");

    const orgName =
        settings?.["organization-name"]?.trim() ||
        (user?.baseUser?.type === "user" && user?.classroom?.class
            ? user.classroom.class
            : orgInfo.name.value || "Organization");

    const alignment = orgInfo.logoPos?.value || "left";
    const reportTitle =
        orgInfo.reportTitle?.value?.trim() || "ACCOUNT BOOK REPORT";
    const showReportTitle = orgInfo.reportTitle?.selected ?? true;
    const showOrgName = orgInfo.name?.selected ?? true;

    const metaLine = useMemo(() => {
        const parts: string[] = [];

        if (orgInfo.address?.selected && orgInfo.address.value?.trim()) {
            parts.push(orgInfo.address.value.trim());
        }
        if (orgInfo.contactNo?.selected && orgInfo.contactNo.value?.trim()) {
            parts.push(orgInfo.contactNo.value.trim());
        }
        if (orgInfo.email?.selected && user?.baseUser?.email) {
            parts.push(user.baseUser.email);
        }

        parts.push(`GENERATED ${formatGeneratedAt()}`);
        return parts.join(" • ");
    }, [orgInfo, user?.baseUser?.email]);

    const layoutClass =
        alignment === "center"
            ? classes.layoutCenter
            : alignment === "right"
            ? classes.layoutRight
            : classes.layoutLeft;

    const centerTextClass =
        alignment === "center"
            ? classes.centerColCenter
            : alignment === "right"
            ? classes.centerColRight
            : classes.centerColLeft;

    useEffect(() => {
        let active = true;
        let objectUrl = "";

        const loadLogo = async () => {
            if (store?.logo) {
                objectUrl = URL.createObjectURL(store.logo);
                if (active) setLogoUrl(objectUrl);
                return;
            }

            const keys = [
                settings?.["organization-logo"],
                user?.classroom?.id
                    ? `${awsSettings.logosDir}/${user.classroom.id}`
                    : null,
            ].filter(Boolean) as string[];

            for (const key of keys) {
                try {
                    const s3Url = await S3Service.getS3ByKey(key);
                    if (!s3Url) continue;
                    const response = await fetch(s3Url);
                    if (!response.ok) continue;
                    const blob = await response.blob();
                    objectUrl = URL.createObjectURL(blob);
                    if (active) setLogoUrl(objectUrl);
                    return;
                } catch (error) {
                    // try next key
                }
            }

            if (active) setLogoUrl("");
        };

        loadLogo();

        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [store?.logo, settings, user?.classroom?.id]);

    const logoNode = logoUrl ? (
        <img src={logoUrl} alt="Logo" className={classes.logo} />
    ) : (
        <Box className={classes.logoPlaceholder} aria-hidden />
    );

    const titleBlock = (
        <Box className={`${classes.centerCol} ${centerTextClass}`}>
            {showReportTitle && (
                <Typography
                    className={`${classes.reportTitle} ${
                        boldPrint ? classes.boldPrintTitle : ""
                    }`}
                >
                    {reportTitle}
                </Typography>
            )}
            <Typography
                className={`${classes.metaLine} ${
                    boldPrint ? classes.boldPrintMeta : ""
                }`}
            >
                {metaLine}
            </Typography>
        </Box>
    );

    const orgNameBlock = !showOrgName ? null : alignment === "center" ? (
        <Typography
            className={`${classes.sideOrgName} ${
                boldPrint ? classes.boldPrintOrg : ""
            }`}
        >
            {orgName}
        </Typography>
    ) : (
        <Box className={classes.rightCol}>
            <Typography
                className={`${classes.sideOrgName} ${
                    boldPrint ? classes.boldPrintOrg : ""
                }`}
            >
                {orgName}
            </Typography>
        </Box>
    );

    return (
        <Box className={`report-header ${classes.root}`}>
            <Box className={layoutClass}>
                <Box className={classes.logoCol}>{logoNode}</Box>
                {titleBlock}
                {orgNameBlock}
            </Box>
            <Box
                className={`${classes.bottomRule} ${
                    boldPrint ? classes.boldPrintRule : ""
                }`}
            />
        </Box>
    );
}
